import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";

const QUICK_LINKS = [
  { href: "/about-us", label: "About Us" },
  { href: "#about", label: "About the Temple" },
  { href: "#story", label: "Baba's Life Story" },
  { href: "#teachings", label: "Teachings" },
  { href: "#devotees", label: "Famous Devotees" },
  { href: "#gallery", label: "Gallery" },
  { href: "#festivals", label: "Festivals & Bhandaras" },
  { href: "#meditations", label: "Osho Meditations" },
  { href: "#donate", label: "Build the New Temple" },
  { href: "#visit", label: "Visit & Seva" },
  { href: "#stay", label: "Stay Overnight" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/newsletter", { name, email });
      toast.success("Subscribed! Satsang news and aarti times will come to your inbox.");
      setEmail("");
      setName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="footer" data-testid="footer" className="bg-[#0C0A09] text-stone-300 border-t border-amber-100/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="relative w-11 h-11 rounded-full overflow-hidden ring-2 ring-amber-500/50 shadow-md shadow-black/40 shrink-0">
                <img src="/images/logo-baba.jpg" alt="Neem Karoli Baba" className="w-full h-full object-cover" />
              </span>
              <div>
                <p className="font-display text-amber-100 font-semibold">Neem Karoli Baba Mandir</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-amber-500/80">Denison · Texas</p>
              </div>
            </div>
            <p className="text-sm text-stone-400 leading-relaxed max-w-sm">
              A home for Maharaj-ji and Lord Hanuman in the Texoma region — open to all,
              feeding all, loving all. Sub Ek — All is One.
            </p>
            <p className="mt-5 flex items-center gap-2 text-sm text-stone-500">
              <MapPin className="h-4 w-4 text-amber-600" /> 1642 FM 1310 · Denison, TX 75020
            </p>
            <a href="tel:+12143956757" data-testid="footer-phone-link" className="mt-2 block text-sm text-stone-500 hover:text-amber-300 transition-colors">
              (214) 395-6757
            </a>
            <a href="mailto:Poorvesh.thakkar@gmail.com" data-testid="footer-email-link" className="mt-1 block text-sm text-stone-500 hover:text-amber-300 transition-colors">
              Poorvesh.thakkar@gmail.com
            </a>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-500 mb-5">Quick Darshan</h3>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="text-sm text-stone-400 hover:text-amber-300 transition-colors">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-500 mb-5">Stay in Satsang</h3>
            <p className="text-sm text-stone-400 mb-4">Aarti times, festival celebrations, and new temple updates — straight to your inbox.</p>
            <form onSubmit={subscribe} className="space-y-3" data-testid="newsletter-form">
              <Input data-testid="newsletter-name-input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                className="h-11 rounded-xl bg-stone-900 border-stone-700 text-stone-200 placeholder:text-stone-500 focus-visible:ring-amber-600" />
              <Input data-testid="newsletter-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="h-11 rounded-xl bg-stone-900 border-stone-700 text-stone-200 placeholder:text-stone-500 focus-visible:ring-amber-600" />
              <Button data-testid="newsletter-submit-button" type="submit" disabled={loading}
                className="w-full h-11 rounded-full bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white font-semibold transition-all duration-300">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Join the Satsang List"}
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-stone-800/80 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-stone-500 text-center sm:text-left">
            Neem Karoli Baba Temple, Denison, Texas — a nonprofit spiritual community. All donations support the temple and its seva.
            <span className="block mt-1.5 text-stone-600">Hanuman Chalisa recitation: Sandeep Khurana, Harmony Voices (CC BY-SA 3.0) · Kainchi Dham photo: Wikimedia Commons (CC BY-SA 4.0)</span>
          </p>
          <p className="font-quote italic text-sm text-amber-500/90">Jai Shri Ram · Jai Hanuman · Jai Maharaj-ji</p>
        </div>
        <div className="mt-4 text-center sm:text-right">
          <a href="/admin" data-testid="footer-admin-link" className="text-[11px] text-stone-600 hover:text-amber-500 transition-colors">
            Temple Admin
          </a>
        </div>
      </div>
    </footer>
  );
}
